import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, DatePicker, message } from "antd";
import {
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../utils/axios.config";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/en_US";
import { format } from "date-fns"; // Add this import
import Swal from "sweetalert2"; // Add this import
import logger from "../../utils/logger";

const { Option } = Select;
const { TextArea } = Input;

const AddPaymentModal = ({
  visible,
  onClose, // Changed from onCancel to onClose to match usage
  onSuccess,
  onPaymentAdded, // Add this prop
  clientData, // Note: parent passes clientId={client?.id}, not clientData object sometimes. We need to handle this.
  clientId, // Add explicit clientId prop
  caseData,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [allCases, setAllCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    date: null,
  });
  const values = form.getFieldsValue();

  // Fetch clients and cases
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [clientsResponse, casesResponse] = await Promise.all([
          axiosInstance.get("/clients"),
          axiosInstance.get("/cases"),
        ]);

        if (clientsResponse.data?.data) {
          setClients(clientsResponse.data.data);
        }

        if (casesResponse.data?.data) {
          setAllCases(casesResponse.data.data);
        }
      } catch (error) {
        logger.error("Error fetching data:", error);
        message.error("خطأ في جلب البيانات");
      } finally {
        setLoading(false);
      }
    };

    if (visible) {
      fetchData();
    }
  }, [visible]);

  // Use client data if provided
  useEffect(() => {
    if (visible) {
      if (clientData?.id) {
          form.setFieldsValue({ client_id: clientData.id });
          // Check if we need to filter cases
          if (allCases.length > 0) {
              handleClientChange(clientData.id);
          }
      } else if (clientId) {
          // If clientId is passed directly
          form.setFieldsValue({ client_id: clientId });
          // Check if we need to filter cases
          if (allCases.length > 0) {
              handleClientChange(clientId);
          }
      }
    }
  }, [visible, clientData, clientId, form, allCases]);

  // Use case data if provided
  useEffect(() => {
    if (visible && caseData) {
      // Pre-fill the payment fields with case data
      const formValues = {
        amount: caseData.agreed_fees,
        description: `دفعة أولية للقضية: ${caseData.title} (${caseData.case_number})`,
        type: "case",
        // Set status based on whether the case exists or not
        status: caseData.status || "pending",
      };

      // If case exists, set the case_id
      if (caseData.id) {
        formValues.case_id = caseData.id;
      }

      form.setFieldsValue(formValues);

      // If we have client data, set the client field
      if (caseData.client_id) {
        form.setFieldsValue({
          client_id: caseData.client_id,
        });

        // Filter cases for this client
        if (allCases.length > 0) {
          handleClientChange(caseData.client_id);
          
          // بعد تحميل قائمة القضايا، قم بتعيين القضية الحالية
          setTimeout(() => {
            form.setFieldsValue({
              case_id: caseData.id,
              status: caseData.status || "pending"
            });
          }, 100);
        }
      }
    }
  }, [visible, caseData, form, allCases]);

  // Handle client selection
  const handleClientChange = (value) => {
    form.setFieldsValue({ case_id: undefined });

    if (!value) {
      setFilteredCases([]);
      return;
    }

    const clientId = value;
    const selectedClient = clients.find((c) => c.id === parseInt(clientId));

    // Filter cases for the selected client
    if (clientId && selectedClient) {
      const clientCases = allCases.filter(
        (caseItem) =>
          caseItem.client_id === parseInt(clientId) ||
          caseItem.client_name === selectedClient.name
      );
      setFilteredCases(clientCases);
    } else {
      setFilteredCases([]);
    }
  };

  const validateForm = () => {
    const values = form.getFieldsValue();
    const newErrors = {};

    // Other validations...

    if (values.type === "case" && !values.case_id) {
      newErrors.case_id = "يرجى اختيار القضية";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);

      // Check if this is a temporary payment (for a case not yet created)
      const isTempPayment = caseData && !caseData.id;

      const paymentData = {
        amount: parseFloat(values.amount || 0).toFixed(2),
        payment_date: values.date
          ? format(new Date(values.date), "yyyy-MM-dd")
          : format(new Date(), "yyyy-MM-dd"),
        payment_type: values.type || "other",
        case_id: values.type === "case" ? values.case_id || caseData?.id : null,
        client_id: values.client_id,
        description: values.description || "",
        status: values.status || "pending",
        // Add temporary case data if case is not yet saved
        temp_case_data: isTempPayment ? caseData : null,
        // Mark as temporary payment
        is_temp_payment: isTempPayment,
      };

      // Show loading indicator
      Swal.fire({
        title: "جاري الحفظ...",
        text: "يرجى الانتظار",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await axiosInstance.post("/payments", paymentData);

      if (response.data) {
        // Show success message with SweetAlert2
        await Swal.fire({
          title: "تم الإضافة!",
          text: "تم إضافة المدفوع بنجاح",
          icon: "success",
          timer: 1500,
        });

        form.resetFields();
        if (typeof onSuccess === "function") onSuccess(response.data);
        if (typeof onPaymentAdded === "function") onPaymentAdded(response.data);
        
        if (onClose && typeof onClose === "function") { // Changed from onCancel to onClose with safety check
          onClose(); // Changed from onCancel() to onClose()
        }
      }
    } catch (error) {
      logger.error("Payment submission error:", error);

      // Show error message with SweetAlert2
      Swal.fire({
        title: "خطأ!",
        text:
          error.response?.data?.message ||
          error.message ||
          "فشل في إضافة المدفوع",
        icon: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDateChange = (date, dateString) => {
    setFormData((prev) => ({
      ...prev,
      date: date,
    }));
  };

  return (
    <Modal
      title={
        <div className="text-xl font-bold text-right text-gray-800 mb-4">
          إضافة دفعة جديدة
        </div>
      }
      open={visible}
      onCancel={onClose} // Changed from onCancel to onClose
      okText="إضافة الدفعة"
      cancelText="إلغاء"
      onOk={() => form.submit()}
      confirmLoading={submitting}
      width={800}
      dir="rtl"
      className="custom-modal"
      bodyStyle={{ maxHeight: "80vh", overflow: "auto", padding: "20px" }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          date: dayjs(),
          type: "case",
          status: "completed",
        }}
        className="px-4"
        requiredMark={false}
        dir="rtl"
        onValuesChange={(changedValues) => {
          // Clear specific errors when fields change
          if ("case_id" in changedValues && changedValues.case_id) {
            setErrors((prev) => {
              const updated = { ...prev };
              delete updated.case_id;
              return updated;
            });
          }

          // Similar for other fields
          if ("client_id" in changedValues && changedValues.client_id) {
            setErrors((prev) => {
              const updated = { ...prev };
              delete updated.client_id;
              return updated;
            });
          }

          if ("amount" in changedValues && changedValues.amount) {
            setErrors((prev) => {
              const updated = { ...prev };
              delete updated.amount;
              return updated;
            });
          }

          // Clear case_id when payment type changes from 'case' to something else
          if ("type" in changedValues && changedValues.type !== "case") {
            form.setFieldsValue({ case_id: undefined });
          }
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Client Selection */}
          <Form.Item
            name="client_id"
            label={
              <span className="text-gray-800 font-semibold">
                اسم العميل <span className="text-red-500">*</span>
              </span>
            }
            rules={[{ required: true, message: "الرجاء اختيار العميل" }]}
            className="mb-3"
          >
            <Select
              showSearch
              placeholder="اختر العميل"
              optionFilterProp="children"
              onChange={handleClientChange}
              loading={loading}
              className="text-right h-11"
              size="large"
              disabled={caseData && caseData.client_id}
              filterOption={(input, option) =>
                (option?.children ?? "").includes(input)
              }
            >
              {clients.map((client) => (
                <Option key={client.id} value={client.id}>
                  {client.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Case Selection */}
          <Form.Item name="case_id">
            <div>
              <label className="block text-sm font-semibold mb-2">
                اختر القضية {values.type === "case"}
              </label>
              <Select
                showSearch
                placeholder={
                  values.client_id
                    ? "-- اختر القضية --"
                    : "-- اختر عميل أولاً --"
                }
                optionFilterProp="children"
                value={values.case_id || undefined}
                onChange={(value) => form.setFieldsValue({ case_id: value })}
                disabled={
                  !values.client_id || loading || values.type !== "case" || (caseData && caseData.id)
                }
                status={
                  errors.case_id && values.type === "case" ? "error" : undefined
                }
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                style={{ width: "100%" }}
                options={filteredCases.map((caseItem) => ({
                  value: caseItem.id.toString(),
                  label: `${caseItem.case_number} - ${
                    caseItem.title || caseItem.case_type
                  }`,
                }))}
                notFoundContent={
                  values.client_id
                    ? "لا توجد قضايا لهذا العميل"
                    : "اختر عميل أولاً"
                }
              />
              {errors.case_id && values.type === "case" && (
                <p className="mt-1 text-sm text-red-500">{errors.case_id}</p>
              )}
            </div>
          </Form.Item>

          {/* Amount */}
          <Form.Item
            name="amount"
            label={
              <span className="text-gray-800 font-semibold">
                المبلغ <span className="text-red-500">*</span>
              </span>
            }
            rules={[
              { required: true, message: "المبلغ مطلوب" },
              {
                type: "number",
                min: 1,
                transform: (value) => Number(value),
                message: "المبلغ يجب أن يكون أكبر من صفر",
              },
            ]}
            className="mb-3"
          >
            <Input
              type="number"
              placeholder="أدخل المبلغ"
              className="text-right h-11"
              size="large"
              suffix="د.ع"
            />
          </Form.Item>

          {/* Date */}
          <Form.Item
            name="date"
            label={<span className="text-gray-800 font-semibold">التاريخ</span>}
            rules={[{ required: true, message: "التاريخ مطلوب" }]}
            className="mb-3"
          >
            <DatePicker
              placeholder="اختر التاريخ"
              style={{ width: "100%" }}
              format="YYYY/MM/DD"
              locale={locale}
              className="text-right h-11"
              size="large"
              value={formData.date}
              onChange={handleDateChange}
              suffixIcon={<CalendarOutlined className="text-primary" />}
            />
          </Form.Item>

          {/* Payment Type */}
          <Form.Item
            name="type"
            label={
              <span className="text-gray-800 font-semibold">نوع الدفعة</span>
            }
            rules={[{ required: true, message: "نوع الدفعة مطلوب" }]}
            className="mb-3"
          >
            <Select
              placeholder="اختر النوع"
              className="text-right ه-11"
              size="large"
            >
              <Option value="case">قضية</Option>
              <Option value="consultation">استشارة</Option>
              <Option value="other">أخرى</Option>
            </Select>
          </Form.Item>

          {/* Payment Status */}
          <Form.Item
            name="status"
            label={
              <span className="text-gray-800 font-semibold">
                الحالة <span className="text-red-500">*</span>
              </span>
            }
            rules={[{ required: true, message: "حالة الدفعة مطلوبة" }]}
            className="mb-3"
          >
            <Select
              placeholder="اختر الحالة"
              className="text-right ه-11"
              size="large"
            >
              <Option value="completed">مكتملة</Option>
              <Option value="pending">معلقة</Option>
            </Select>
          </Form.Item>

          {/* Description */}
          <Form.Item
            name="description"
            label={<span className="text-gray-800 font-semibold">الوصف</span>}
            className="mb-3 md:col-span-2"
          >
            <TextArea
              placeholder="وصف اختياري للدفعة..."
              className="text-right"
              rows={4}
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default AddPaymentModal;
